import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { apiFetch } from "../lib/api";
// GuestLibraryModal removed - guests now have temporary library access
import { Folder } from "../components/DraggableFolderList";
import SharedFolderSidebar from "../components/SharedFolderSidebar";
import { Thumbnail } from "../components/Thumbnail";
import { getItemThumbnailUrl, sanitizeThumbnailUrl } from "../lib/thumbnailUrl";
import { buildDocxBlob, docxFilenameFromTitle } from "../lib/wordExport";

interface ContextMenuState {
  itemId: string;
  x: number;
  y: number;
  showFolders: boolean;
}

// Folder interface imported from DraggableFolderList

interface LibraryItem {
  id: string;
  title: string;
  created_at: string;
  published_date?: string;
  duration_ms?: number;
  transcript_json?: string;
  video_id?: string;
  channel_name?: string;
  source?: string;
  source_type?: string;
  thumbnail_url?: string;
  channel_artwork_url?: string;
  youtube_url?: string;
  was_truncated?: boolean;
  _durationStr?: string;
}

function computeDurationMs(item: LibraryItem): number {
  if (item.duration_ms && Number.isFinite(item.duration_ms) && item.duration_ms > 0) {
    return item.duration_ms;
  }
  if (item.transcript_json) {
    try {
      const segs: Segment[] = JSON.parse(item.transcript_json);
      const last = segs[segs.length - 1];
      if (last?.end_ms) return last.end_ms;
    } catch { /* ignore */ }
  }
  return 0;
}

function formatDuration(ms: number): string {
  if (!ms || !Number.isFinite(ms)) return "-";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function formatDate(iso: string): string {
  if (!iso) return "-";
  // For bare date strings like "2024-11-05", append T12:00:00
  // to avoid UTC-to-local offset shifting the day
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso + "T12:00:00" : iso;
  return new Date(normalized).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

type ExportFormat = "txt" | "srt" | "vtt" | "json";

interface Segment {
  speaker: string;
  text: string;
  start_ms?: number;
  end_ms?: number;
}

interface TopicSearchResult {
  item: {
    id: string;
    title: string;
    channel_name: string;
    duration_ms: number;
    created_at: string;
    video_id: string;
    thumbnail_url: string;
    published_date: string | null;
  };
  segment: {
    id: string;
    topic: string;
    start_ms: number;
    end_ms: number;
    summary: string;
    speakers: string[];
  };
}

function msToTimestamp(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function buildExportContent(
  format: ExportFormat,
  segments: Segment[],
  title: string,
  durationMs: number,
): { content: string; filename: string } {
  const lines = segments.map((seg) => ({
    speaker: seg.speaker,
    text: seg.text,
    timestamp: msToTimestamp(seg.start_ms || 0),
  }));

  let content = "";
  const filename = `${title}.${format}`;

  switch (format) {
    case "txt":
      content = lines.map((l) => `${l.speaker}: ${l.text}`).join("\n\n");
      break;
    case "srt":
      content = lines
        .map((l, i) => {
          const end = lines[i + 1]?.timestamp || msToTimestamp(durationMs);
          return `${i + 1}\n${l.timestamp},000 --> ${end},000\n${l.speaker}: ${l.text}\n`;
        })
        .join("\n");
      break;
    case "vtt":
      content =
        "WEBVTT\n\n" +
        lines
          .map((l, i) => {
            const end = lines[i + 1]?.timestamp || msToTimestamp(durationMs);
            return `${l.timestamp}.000 --> ${end}.000\n${l.speaker}: ${l.text}`;
          })
          .join("\n\n");
      break;
    case "json":
      content = JSON.stringify({ transcript: lines }, null, 2);
      break;
  }

  return { content, filename };
}

function downloadBlob(content: string, filename: string) {
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function LibraryPage() {
  const { user, loading: authLoading, isSessionExpired } = useAuth();
  const navigate = useNavigate();
  const [pageSearchParams] = useSearchParams();

  const [folders, setFolders] = useState<Folder[]>([]);
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  // Default collapsed on mobile (< 640px)
  const [foldersCollapsed, setFoldersCollapsed] = useState(() => 
    typeof window !== 'undefined' && window.innerWidth < 640
  );
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [bulkCopied, setBulkCopied] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
  // Bulk-move dropdown: search + per-section expansion (Channels collapsed by default — typically 100+)
  const [bulkMoveSearch, setBulkMoveSearch] = useState("");
  const [bulkMoveCustomExpanded, setBulkMoveCustomExpanded] = useState(true);
  const [bulkMoveFavoritesExpanded, setBulkMoveFavoritesExpanded] = useState(true);
  const [bulkMoveChannelsExpanded, setBulkMoveChannelsExpanded] = useState(false);
  const contextRef = useRef<HTMLDivElement>(null);
  const bulkMoveRef = useRef<HTMLDivElement>(null);
  const moveDropdownRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [topicSearchMode, setTopicSearchMode] = useState(false);
  const [privateFilter, setPrivateFilter] = useState(false);
  const [topicResults, setTopicResults] = useState<TopicSearchResult[]>([]);
  const [topicSearching, setTopicSearching] = useState(false);
  const [titleSearchResults, setTitleSearchResults] = useState<LibraryItem[]>([]);
  const [titleSearching, setTitleSearching] = useState(false);
  const [selectedTopicResult, setSelectedTopicResult] = useState<TopicSearchResult | null>(null);
  const [selectedTopicItems, setSelectedTopicItems] = useState<Set<string>>(new Set());
  const [_segmentQuotes, setSegmentQuotes] = useState<{ speaker: string; text: string }[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; title: string } | null>(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [moveDropdownId, setMoveDropdownId] = useState<string | null>(null);
  const [isGuestLibrary, setIsGuestLibrary] = useState(false);
  const [guestNotice, setGuestNotice] = useState<string | null>(null);

  // Inline "+ New folder" creation from the sidebar (always-visible affordance)
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  
  // Pagination state
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const PAGE_SIZE = 50;

  const fetchFolders = useCallback(async () => {
    const data = await apiFetch<Folder[] | { folders: Folder[] }>("/folders");
    const folderList = Array.isArray(data) ? data : data.folders || [];
    setFolders(folderList);
  }, []);

  // Create a custom folder from the sidebar inline input. Empty name = cancel (no API call).
  const handleCreateFolder = useCallback(async () => {
    const name = newFolderName.trim();
    if (!name) {
      setCreatingFolder(false);
      setNewFolderName("");
      return;
    }
    await apiFetch("/folders", {
      method: "POST",
      body: JSON.stringify({ name }),
    });
    await fetchFolders();
    setNewFolderName("");
    setCreatingFolder(false);
  }, [newFolderName, fetchFolders]);

  const fetchItems = useCallback(async (folderId: string | null, offset = 0, append = false) => {
    const path = folderId
      ? `/library/items?folder_id=${folderId}&limit=${PAGE_SIZE}&offset=${offset}`
      : `/library/items?limit=${PAGE_SIZE}&offset=${offset}`;
    const data = await apiFetch<LibraryItem[] | { items: LibraryItem[]; is_guest?: boolean; guest_notice?: string; has_more?: boolean; total?: number }>(path);
    const itemList = Array.isArray(data) ? data : data.items || [];
    
    // Accumulate or replace items
    setItems(prev => append ? [...prev, ...itemList] : itemList);
    setHasMore(!Array.isArray(data) && !!data.has_more);
    setCurrentOffset(offset + itemList.length);
    
    // Handle guest session response
    if (!Array.isArray(data)) {
      setIsGuestLibrary(!!data.is_guest);
      setGuestNotice(data.guest_notice || null);
    }
  }, []);

  useEffect(() => {
    console.log("[LibraryPage] useEffect triggered, user:", !!user);
    if (authLoading) return;

    setLoading(true);
    console.log("[LibraryPage] Fetching items...");
    
    // Fetch items for both users and guests
    const fetchPromises = user
      ? [fetchFolders(), fetchItems(null)]  // User: folders + items
      : [fetchItems(null)];  // Guest: items only (no folders)
    
    Promise.all(fetchPromises)
      .then(() => console.log("[LibraryPage] Fetch complete"))
      .catch((err) => console.error("[LibraryPage] Fetch error:", err))
      .finally(() => setLoading(false));
  }, [user, authLoading, fetchFolders, fetchItems]);

  // Read folder param from URL on mount (for back navigation from transcript view)
  useEffect(() => {
    const folderParam = pageSearchParams.get('folder');
    if (folderParam && folders.length > 0 && selectedFolderId === null) {
      selectFolder(folderParam);
    }
  }, [folders, pageSearchParams]);

  // Close menus on outside click / Escape
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (contextMenu && contextRef.current && !contextRef.current.contains(e.target as Node)) {
        setContextMenu(null);
      }
      if (bulkMoveOpen && bulkMoveRef.current && !bulkMoveRef.current.contains(e.target as Node)) {
        setBulkMoveOpen(false);
        setBulkMoveSearch("");
      }
      if (moveDropdownId && moveDropdownRef.current && !moveDropdownRef.current.contains(e.target as Node)) {
        setMoveDropdownId(null);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setContextMenu(null);
        setBulkMoveOpen(false);
        setBulkMoveSearch("");
        setMoveDropdownId(null);
      }
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [contextMenu, bulkMoveOpen]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Topic search effect
  useEffect(() => {
    if (!topicSearchMode || !debouncedQuery || debouncedQuery.length < 2) {
      setTopicResults([]);
      setSelectedTopicResult(null);
      setSelectedTopicItems(new Set());
      return;
    }
    
    const searchTopics = async () => {
      setTopicSearching(true);
      setSelectedTopicResult(null);
      setSelectedTopicItems(new Set()); // Clear selections on new search
      try {
        const folderParam = selectedFolderId ? `&folder_id=${selectedFolderId}` : '';
        const data = await apiFetch<{ results: TopicSearchResult[]; query: string; count: number }>(
          `/library/search?q=${encodeURIComponent(debouncedQuery)}&limit=50${folderParam}`
        );
        setTopicResults(data.results || []);
        // Auto-select first result
        if (data.results && data.results.length > 0) {
          setSelectedTopicResult(data.results[0]);
        }
      } catch (err) {
        console.error("Topic search failed:", err);
        setTopicResults([]);
      } finally {
        setTopicSearching(false);
      }
    };
    
    searchTopics();
  }, [topicSearchMode, debouncedQuery]);

  // Title search effect - uses backend search with mode=title
  useEffect(() => {
    if (topicSearchMode || !debouncedQuery || debouncedQuery.length < 2) {
      setTitleSearchResults([]);
      return;
    }
    
    const searchTitles = async () => {
      setTitleSearching(true);
      try {
        const folderParam = selectedFolderId ? `&folder_id=${selectedFolderId}` : '';
        const data = await apiFetch<{ results: { item: LibraryItem }[]; query: string; count: number }>(
          `/library/search?q=${encodeURIComponent(debouncedQuery)}&mode=title&limit=50${folderParam}`
        );
        setTitleSearchResults((data.results || []).map(r => r.item));
      } catch (err) {
        console.error("Title search failed:", err);
        setTitleSearchResults([]);
      } finally {
        setTitleSearching(false);
      }
    };
    
    searchTitles();
  }, [topicSearchMode, debouncedQuery, selectedFolderId]);

  // Fetch quotes when a topic result is selected
  useEffect(() => {
    if (!selectedTopicResult) {
      setSegmentQuotes([]);
      return;
    }
    
    const fetchQuotes = async () => {
      try {
        const response = await apiFetch<{ item: { transcript_json?: string } }>(
          `/library/items/${selectedTopicResult.item.id}`
        );
        
        if (response.item?.transcript_json) {
          const segments: { speaker: string; text: string; start_ms: number }[] = JSON.parse(response.item.transcript_json);
          // Get quotes within the topic segment's time range
          const quotes = segments
            .filter(s => s.start_ms >= selectedTopicResult.segment.start_ms && 
                        s.start_ms < selectedTopicResult.segment.end_ms)
            .slice(0, 3)  // First 3 quotes
            .map(s => ({ speaker: s.speaker, text: s.text }));
          setSegmentQuotes(quotes);
        }
      } catch {
        setSegmentQuotes([]);
      }
    };
    
    fetchQuotes();
  }, [selectedTopicResult]);

  // Guest modal removed - guests now have temporary library access with 24h expiry

  // Filter items by search query and precompute durations
  // NOTE: Must be called before early returns to maintain hooks order
  const filteredItems = useMemo(() => {
    const q = debouncedQuery?.toLowerCase() || "";
    return items
      .filter((item) => {
        // Private filter: only show uploads
        if (privateFilter && item.source_type !== 'upload') {
          return false;
        }
        if (!q) return true;
        return (
          item.title.toLowerCase().includes(q) ||
          (item.channel_name?.toLowerCase().includes(q) ?? false)
        );
      })
      .map((item) => ({
        ...item,
        _durationMs: computeDurationMs(item),
        _durationStr: formatDuration(computeDurationMs(item)),
      }));
  }, [items, debouncedQuery, privateFilter]);

  // Issue 4: Auto-select folder from URL param when returning from transcript view
  // Must be before early returns to satisfy Rules of Hooks
  useEffect(() => {
    const folderParam = pageSearchParams.get('folder');
    if (folderParam && folders.length > 0 && selectedFolderId === null) {
      selectFolder(folderParam);
    }
  }, [folders, pageSearchParams]);

  // Compute allSelected based on mode
  const allSelected = topicSearchMode && topicResults.length > 0
    ? topicResults.length > 0 && selectedTopicItems.size === new Set(topicResults.map(r => r.item.id)).size
    : filteredItems.length > 0 && selectedIds.size === filteredItems.length;

  if (authLoading) return null;

  // For guests with no items, show sign-up prompt
  if (!user && !loading && items.length === 0 && !isSessionExpired) {
    return (
      <div className="flex h-[calc(100vh-68px)] flex-col items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="mx-auto h-16 w-16 text-gray-300"
          >
            <path d="M19.5 21a3 3 0 0 0 3-3v-4.5a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h15ZM1.5 10.146V6a3 3 0 0 1 3-3h5.379a2.25 2.25 0 0 1 1.59.659l2.122 2.121c.14.141.331.22.53.22H19.5a3 3 0 0 1 3 3v1.146A4.483 4.483 0 0 0 19.5 9h-15a4.483 4.483 0 0 0-3 1.146Z" />
          </svg>
          <h1 className="mt-4 text-xl font-semibold text-gray-900">Your Library</h1>
          <p className="mt-2 text-sm text-gray-500">Transcribe something to see it here, or sign up to save your transcripts permanently</p>
        </div>
      </div>
    );
  }
  
  // Guest library banner component
  const GuestLibraryBanner = () => (
    <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-4">
      <div className="flex items-start gap-3">
        <svg className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div className="flex-1">
          <p className="text-sm font-medium text-amber-800">Temporary Library</p>
          <p className="text-sm text-amber-700 mt-1">
            {guestNotice || "Your transcriptions will be deleted in 24 hours. Sign up to save them permanently."}
          </p>
        </div>
        <button
          onClick={() => navigate("/signup")}
          className="shrink-0 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700"
        >
          Sign up
        </button>
      </div>
    </div>
  );


  // Session expired banner component (shown when a previously-authenticated session has expired)
  const SessionExpiredBanner = () => (
    <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-4">
      <div className="flex items-start gap-3">
        <svg className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div className="flex-1">
          <p className="text-sm font-medium text-amber-800">Session Expired</p>
          <p className="text-sm text-amber-700 mt-1">
            Your session has expired. Please log in again to access your library.
          </p>
        </div>
        <button
          onClick={() => navigate("/login")}
          className="shrink-0 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700"
        >
          Log in
        </button>
      </div>
    </div>
  );

  async function selectFolder(folderId: string | null) {
    setSelectedFolderId(folderId);
    setCurrentOffset(0);
    setLoading(true);
    try {
      await fetchItems(folderId, 0, false);
    } finally {
      setLoading(false);
    }
  }
  
  async function loadMore() {
    setLoadingMore(true);
    try {
      let offset = currentOffset;
      let keepGoing = true;

      while (keepGoing) {
        const path = selectedFolderId
          ? `/library/items?folder_id=${selectedFolderId}&limit=${PAGE_SIZE}&offset=${offset}`
          : `/library/items?limit=${PAGE_SIZE}&offset=${offset}`;
        const data = await apiFetch<LibraryItem[] | { items: LibraryItem[]; has_more?: boolean }>(path);
        const itemList = Array.isArray(data) ? data : data.items || [];

        // Append batch to UI so the list grows progressively as data arrives
        setItems(prev => [...prev, ...itemList]);
        offset += itemList.length;

        // Continue only if backend reports more AND we actually received items
        keepGoing = !Array.isArray(data) && !!data.has_more && itemList.length > 0;
      }

      // Final state: everything loaded, hide the button
      setHasMore(false);
      setCurrentOffset(offset);
    } finally {
      setLoadingMore(false);
    }
  }

  async function deleteFolder(id: string) {
    await apiFetch(`/folders/${id}`, { method: "DELETE" });
    await fetchFolders();
    if (selectedFolderId === id) {
      setSelectedFolderId(null);
      await fetchItems(null);
    }
  }

  async function deleteItem(id: string) {
    await apiFetch(`/library/items/${id}`, { method: "DELETE" });
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    await fetchItems(selectedFolderId);
  }

  async function moveToFolder(itemId: string, folderId: string) {
    await apiFetch(`/folders/${folderId}/items`, {
      method: "POST",
      body: JSON.stringify({ item_id: itemId }),
    });
    setContextMenu(null);
    await Promise.all([fetchFolders(), fetchItems(selectedFolderId)]);
  }

  async function bulkMoveToFolder(folderId: string) {
    if (selectedIds.size === 0) return;
    for (const itemId of selectedIds) {
      await apiFetch(`/folders/${folderId}/items`, {
        method: "POST",
        body: JSON.stringify({ item_id: itemId }),
      });
    }
    setBulkMoveOpen(false);
    setBulkMoveSearch("");
    setSelectedIds(new Set());
    await Promise.all([fetchFolders(), fetchItems(selectedFolderId)]);
  }

  function deleteSelected() {
    if (selectedIds.size === 0) return;
    setBulkDeleteConfirm(true);
  }

  async function confirmBulkDelete() {
    for (const itemId of selectedIds) {
      await apiFetch(`/library/items/${itemId}`, { method: "DELETE" });
    }
    setSelectedIds(new Set());
    setBulkDeleteConfirm(false);
    await fetchItems(selectedFolderId);
  }

  async function removeFromFolder(itemId: string) {
    if (!selectedFolderId) return;
    await apiFetch(`/folders/${selectedFolderId}/items/${itemId}`, {
      method: "DELETE",
    });
    await Promise.all([fetchFolders(), fetchItems(selectedFolderId)]);
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (topicSearchMode && topicResults.length > 0) {
      // Topic mode: toggle topic item selection
      const topicItemIds = new Set(topicResults.map(r => r.item.id));
      if (selectedTopicItems.size === topicItemIds.size) {
        setSelectedTopicItems(new Set());
      } else {
        setSelectedTopicItems(topicItemIds);
      }
    } else {
      // Title mode: toggle regular selection
      if (allSelected) {
        setSelectedIds(new Set());
      } else {
        setSelectedIds(new Set(filteredItems.map((i) => i.id)));
      }
    }
  }

  async function exportSelected(format: ExportFormat) {
    if (selectedIds.size === 0) return;
    setExporting(true);
    try {
      for (const itemId of selectedIds) {
        const res = await apiFetch<any>(`/library/items/${itemId}`);
        const item = res.item || res;
        const segments: Segment[] = item.transcript_json
          ? JSON.parse(item.transcript_json)
          : [];
        const lastSeg = segments[segments.length - 1];
        const durationMs = lastSeg?.end_ms || 0;
        const { content, filename } = buildExportContent(
          format,
          segments,
          item.title || "transcript",
          durationMs,
        );
        downloadBlob(content, filename);
      }
    } finally {
      setExporting(false);
    }
  }

  function buildMarkdownForItem(
    title: string,
    segments: Segment[],
    durationMs: number,
    channelName?: string,
  ): string {
    const names = Array.from(new Set(segments.map((s) => s.speaker)));
    const wc = segments.reduce((acc, s) => acc + s.text.split(" ").length, 0);

    let md = `# ${title}\n\n`;
    if (channelName) md += `**Channel:** ${channelName}\n`;
    md += `**Duration:** ${formatDuration(durationMs)}\n`;
    md += `**Words:** ${wc}\n`;
    md += `**Speakers:** ${names.length}\n\n`;

    md += `## Speakers\n\n`;
    names.forEach((name) => {
      md += `- ${name}\n`;
    });
    md += `\n## Transcript\n\n`;
    segments.forEach((seg) => {
      md += `**${seg.speaker}** _(${msToTimestamp(seg.start_ms || 0)})_\n${seg.text}\n\n`;
    });
    return md.trim();
  }

  async function copySelected() {
    if (selectedIds.size === 0) return;
    setExporting(true);
    try {
      const parts: string[] = [];
      for (const itemId of selectedIds) {
        const res = await apiFetch<any>(`/library/items/${itemId}`);
        const item = res.item || res;
        const segments: Segment[] = item.transcript_json
          ? JSON.parse(item.transcript_json)
          : [];
        const lastSeg = segments[segments.length - 1];
        const durationMs = item.duration_ms || lastSeg?.end_ms || 0;
        parts.push(buildMarkdownForItem(item.title || "Transcript", segments, durationMs, item.channel_name));
      }
      await navigator.clipboard.writeText(parts.join("\n\n---\n\n"));
      setBulkCopied(true);
      setTimeout(() => setBulkCopied(false), 2000);
    } finally {
      setExporting(false);
    }
  }

  async function aiReadyExportSelected() {
    if (selectedIds.size === 0) return;
    setExporting(true);
    try {
      const parts: string[] = [];
      for (const itemId of selectedIds) {
        const res = await apiFetch<any>(`/library/items/${itemId}`);
        const item = res.item || res;
        const segments: Segment[] = item.transcript_json
          ? JSON.parse(item.transcript_json)
          : [];
        const lastSeg = segments[segments.length - 1];
        const durationMs = item.duration_ms || lastSeg?.end_ms || 0;
        parts.push(buildMarkdownForItem(item.title || "Transcript", segments, durationMs, item.channel_name));
      }
      const md = parts.join("\n\n---\n\n");
      downloadBlob(md, selectedIds.size === 1 ? "transcript.md" : "transcripts.md");
    } finally {
      setExporting(false);
    }
  }


  async function docxExportSelected() {
    if (selectedIds.size === 0) return;
    setExporting(true);
    try {
      for (const itemId of selectedIds) {
        const res = await apiFetch<any>(`/library/items/${itemId}`);
        const item = res.item || res;
        const segments: Segment[] = item.transcript_json
          ? JSON.parse(item.transcript_json)
          : [];
        const segs = segments.map((s) => ({
          speaker: s.speaker,
          text: s.text,
          timestamp: msToTimestamp(s.start_ms || 0),
        }));
        const blob = await buildDocxBlob({ title: item.title || "Transcript", segments: segs });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = docxFilenameFromTitle(item.title || "Transcript");
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } finally {
      setExporting(false);
    }
  }

  async function moveItemToFolder(itemId: string, folderId: string) {
    await apiFetch(`/folders/${folderId}/items`, {
      method: "POST",
      body: JSON.stringify({ item_id: itemId }),
    });
    setMoveDropdownId(null);
    await Promise.all([fetchFolders(), fetchItems(selectedFolderId)]);
  }

  async function confirmDelete() {
    if (!deleteConfirm) return;
    await deleteItem(deleteConfirm.id);
    setDeleteConfirm(null);
  }

  return (
    <div className="flex h-[calc(100vh-68px)] flex-col md:flex-row">
      {/* Sidebar - only for authenticated users, show after folders loaded */}
      {user && !loading && (
        <aside className="flex w-full shrink-0 flex-col border-b border-r-0 bg-gray-50 p-4 md:h-full md:w-64 md:border-b-0 md:border-r md:overflow-hidden">
          {/* Mobile: collapsible header */}
          <div className="flex items-center justify-between md:hidden mb-2">
            <button
              onClick={() => selectFolder(null)}
              className={`flex-1 min-h-[44px] rounded-md px-3 py-2 text-left text-sm font-medium ${
                selectedFolderId === null
                  ? "bg-blue-100 text-blue-700"
                  : "text-gray-700"
              }`}
            >
              All Transcripts
            </button>
            <button
              onClick={() => setFoldersCollapsed(!foldersCollapsed)}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-500"
              aria-label={foldersCollapsed ? "Expand folders" : "Collapse folders"}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className={`h-5 w-5 transition-transform ${foldersCollapsed ? "" : "rotate-180"}`}
              >
                <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          
          {/* Desktop: always show All Transcripts button */}
          <button
            onClick={() => selectFolder(null)}
            onTouchEnd={(e) => {
              e.preventDefault();
              selectFolder(null);
            }}
            className={`mb-1 w-full min-h-[44px] rounded-md px-3 py-2 text-left text-sm font-medium touch-manipulation hidden md:block ${
              selectedFolderId === null
                ? "bg-blue-100 text-blue-700"
                : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
            }`}
          >
            All Transcripts
          </button>

          {/* Folders section - collapsible on mobile */}
          <div className={`${foldersCollapsed ? "hidden" : ""} md:block`}>
            {/* Always-visible "+ New folder" affordance (works even with zero custom folders) */}
            <div className="mt-1">
              {!creatingFolder ? (
                <button
                  onClick={() => setCreatingFolder(true)}
                  className="w-full min-h-[44px] rounded-md px-3 py-2 text-left text-sm font-medium text-gray-600 hover:bg-gray-100 active:bg-gray-200 flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                    <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                  </svg>
                  New folder
                </button>
              ) : (
                <input
                  type="text"
                  autoFocus
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onBlur={handleCreateFolder}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleCreateFolder();
                    if (e.key === "Escape") {
                      setNewFolderName("");
                      setCreatingFolder(false);
                    }
                  }}
                  placeholder="Folder name..."
                  className="w-full min-h-[44px] rounded-md border border-blue-300 px-3 py-2 text-sm text-gray-900 outline-none focus:ring-1 focus:ring-blue-400"
                />
              )}
            </div>

            <div className="my-3 h-px bg-gray-200" />

            <SharedFolderSidebar
            mode="library"
            folders={folders}
            selectedFolderId={selectedFolderId}
            onSelectFolder={selectFolder}
            onDeleteFolder={deleteFolder}
            onFoldersReordered={fetchFolders}
            onCreateFolder={async (name) => {
              await apiFetch("/folders", {
                method: "POST",
                body: JSON.stringify({ name }),
              });
              await fetchFolders();
            }}
            collapsed={foldersCollapsed}
            onToggleCollapse={() => setFoldersCollapsed(!foldersCollapsed)}
          />
          </div>
        </aside>
      )}

      {/* Main area */}
      <main className="flex-1 overflow-y-auto p-6">
        {/* Guest library / session-expired banner */}
        {isSessionExpired ? <SessionExpiredBanner /> : isGuestLibrary ? <GuestLibraryBanner /> : null}
        
        <div className="mb-6 flex flex-col gap-3">
          {/* Title */}
          <h1 className="text-xl font-semibold text-gray-900">
            {selectedFolderId
              ? folders.find((f) => f.id === selectedFolderId)?.name ?? "Folder"
              : "All Transcripts"}
          </h1>
          
          {/* Search bar with mode toggles on right */}
          <div className="flex items-center gap-2 max-w-lg">
            <div className="relative flex-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              >
                <path
                  fillRule="evenodd"
                  d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                  clipRule="evenodd"
                />
              </svg>
              <input
                type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={topicSearchMode ? "Search by topic (e.g., Greenland, AI, Ukraine)..." : "Search transcripts..."}
              className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            </div>
            
            {/* Search mode toggles */}
            <button
              onClick={() => {
                setTopicSearchMode(false);
                setTopicResults([]);
              }}
              className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                !topicSearchMode
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
              }`}
            >
              Title
            </button>
            <button
              onClick={() => {
                setTopicSearchMode(true);
                setTopicResults([]);
              }}
              className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                topicSearchMode
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
              }`}
            >
              Topic
            </button>
            <button
              onClick={() => setPrivateFilter(!privateFilter)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                privateFilter
                  ? "bg-purple-600 text-white border-purple-600"
                  : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
              }`}
              title="Filter to show only private uploads"
            >
              Private
            </button>
          </div>
        </div>

        {selectedIds.size > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border bg-gray-50 px-3 py-2">
            <span className="text-sm font-medium text-gray-700">
              {selectedIds.size} selected
            </span>

            {/* Copy */}
            <button
              onClick={copySelected}
              disabled={exporting}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {bulkCopied ? "Copied!" : "Copy"}
            </button>

            {/* AI Ready */}
            <button
              onClick={aiReadyExportSelected}
              disabled={exporting}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              AI Ready
            </button>

            {/* DOCX */}
            <button
              onClick={docxExportSelected}
              disabled={exporting}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >Word</button>

            {/* Export formats */}
            {(["txt", "srt", "vtt", "json"] as ExportFormat[]).map((fmt) => (
              <button
                key={fmt}
                onClick={() => exportSelected(fmt)}
                disabled={exporting}
                className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {fmt.toUpperCase()}
              </button>
            ))}
            {exporting && (
              <span className="text-xs text-gray-500">...</span>
            )}

            {/* Chat with selected */}
            <button
              onClick={() => {
                const ids = Array.from(selectedIds).join(',');
                const selectedItems = items
                  .filter((i) => selectedIds.has(i.id))
                  .map((i) => ({ id: i.id, title: i.title }));
                navigate(`/chat?items=${ids}`, { state: { items: selectedItems } });
              }}
              className="px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              Chat
            </button>

            {/* Move + Delete pushed to the right on desktop */}
            <div className="md:ml-auto flex flex-wrap items-center gap-2">
              {/* Move to folder */}
              <div className="relative" ref={bulkMoveRef}>
                <button
                  onClick={() => setBulkMoveOpen(!bulkMoveOpen)}
                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Move
                </button>
                {bulkMoveOpen && (
                  <div className="absolute right-0 z-20 mt-1 w-64 max-h-96 overflow-y-auto rounded-md border bg-white shadow-lg">
                    {/* Sticky search at top */}
                    <div className="sticky top-0 bg-white border-b px-2 py-2">
                      <input
                        type="text"
                        autoFocus
                        value={bulkMoveSearch}
                        onChange={(e) => setBulkMoveSearch(e.target.value)}
                        placeholder="Search folders..."
                        className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    {/* Grouped sections */}
                    {(() => {
                      const q = bulkMoveSearch.trim().toLowerCase();
                      const matches = (f: Folder) => !q || f.name.toLowerCase().includes(q);

                      const customs = folders.filter((f) => f.folder_type === "custom" && matches(f));
                      const favorites = folders.filter((f) => f.folder_type === "subscribed" && matches(f));
                      const channels = folders.filter(
                        (f) => (f.folder_type === "channel" || f.folder_type === "auto" || !f.folder_type) && matches(f)
                      );

                      const renderSection = (
                        label: string,
                        sectionItems: Folder[],
                        expanded: boolean,
                        setExpanded: (v: boolean) => void
                      ) => {
                        // Hide entirely empty sections; when searching, hide if no matches
                        if (sectionItems.length === 0) return null;
                        return (
                          <div>
                            <button
                              type="button"
                              onClick={() => setExpanded(!expanded)}
                              className="flex items-center gap-1.5 w-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 hover:bg-gray-50"
                            >
                              <svg className={`h-3 w-3 transition-transform ${expanded ? "rotate-90" : ""}`} viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z" clipRule="evenodd" />
                              </svg>
                              <span className="flex-1 text-left">{label}</span>
                              <span className="font-normal normal-case text-gray-400">({sectionItems.length})</span>
                            </button>
                            {expanded && sectionItems.map((f) => (
                              <button
                                key={f.id}
                                onClick={() => bulkMoveToFolder(f.id)}
                                className="block w-full px-5 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100 truncate"
                                title={f.name}
                              >
                                {f.name}
                              </button>
                            ))}
                          </div>
                        );
                      };

                      const hasAny = customs.length + favorites.length + channels.length > 0;

                      return (
                        <>
                          {renderSection("Custom", customs, bulkMoveCustomExpanded, setBulkMoveCustomExpanded)}
                          {renderSection("Favorites", favorites, bulkMoveFavoritesExpanded, setBulkMoveFavoritesExpanded)}
                          {renderSection("Channels", channels, bulkMoveChannelsExpanded, setBulkMoveChannelsExpanded)}
                          {!hasAny && (
                            <p className="px-3 py-3 text-xs text-gray-400 text-center">
                              {q ? `No folders matching "${q}"` : "No folders"}
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Delete */}
              <button
                onClick={deleteSelected}
                className="px-3 py-1.5 text-xs font-medium rounded-md border border-red-200 bg-white text-red-600 hover:bg-red-50 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        )}

        {loading || titleSearching ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
          </div>
        ) : (!topicSearchMode && debouncedQuery.length >= 2 && titleSearchResults.length === 0) ? (
          <p className="py-20 text-center text-sm text-gray-400">
            No matching transcripts.
          </p>
        ) : filteredItems.length === 0 && !(topicSearchMode && debouncedQuery.length >= 2) && !(!topicSearchMode && debouncedQuery.length >= 2) ? (
          <p className="py-20 text-center text-sm text-gray-400">
            {debouncedQuery ? "No matching transcripts." : "No transcripts yet."}
          </p>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-3 px-4 py-1">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-xs font-medium text-gray-500">
                Select all
              </span>
            </div>
            {/* Chat with selected - Topic Search bulk action */}
            {topicSearchMode && topicResults.length > 0 && selectedTopicItems.size > 0 && (
              <div className="mb-3 flex items-center gap-2 rounded-lg border bg-gray-50 px-3 py-2">
                <span className="min-h-[44px] flex items-center text-sm font-medium text-gray-700">
                  {selectedTopicItems.size} episode{selectedTopicItems.size !== 1 ? 's' : ''} selected
                </span>
                <button
                  onClick={() => {
                    const ids = Array.from(selectedTopicItems).join(',');
                    const selectedItems = topicResults
                      .filter(r => selectedTopicItems.has(r.item.id))
                      .map(r => ({ id: r.item.id, title: r.item.title }));
                    // Deduplicate by id (same episode can appear multiple times in topic results)
                    const uniqueItems = Array.from(
                      new Map(selectedItems.map(i => [i.id, i])).values()
                    );
                    navigate(`/chat?items=${ids}`, { state: { items: uniqueItems } });
                  }}
                  className="min-h-[44px] rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  Chat with selected
                </button>
              </div>
            )}
            {/* Topic Search Results - Two Column Layout */}
            {topicSearchMode && debouncedQuery.length >= 2 && (
              topicSearching ? (
                <div className="py-8 text-center text-gray-500">
                  <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                  <p className="mt-2 text-sm">Searching topics...</p>
                </div>
              ) : topicResults.length === 0 ? (
                <div className="py-8 text-center text-gray-500">
                  <p className="text-sm">No topics found for "{debouncedQuery}"</p>
                  <p className="mt-1 text-xs">Try different keywords or switch to title search</p>
                </div>
              ) : (
                <div className="flex gap-4">
                  {/* Left: Compact topic list */}
                  <div className="flex-1 space-y-2 min-w-0">
                    {topicResults.map((result) => (
                      <div
                        key={`${result.item.id}-${result.segment.start_ms}`}
                        className={`w-full flex items-center gap-3 rounded-lg border px-3 py-2 text-left transition hover:shadow-sm cursor-pointer ${
                          selectedTopicResult?.item.id === result.item.id && selectedTopicResult?.segment.start_ms === result.segment.start_ms
                            ? "border-blue-500 bg-blue-50"
                            : selectedTopicItems.has(result.item.id)
                            ? "border-blue-300 bg-blue-50/50"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        }`}
                        onClick={() => {
                          // Desktop: show detail panel; Mobile: navigate directly
                          if (window.innerWidth >= 768) {
                            setSelectedTopicResult(result);
                          } else {
                            navigate(`/transcript/${result.item.id}?t=${result.segment.start_ms}${selectedFolderId ? `&from_folder=${selectedFolderId}` : ''}`);
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedTopicItems.has(result.item.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            setSelectedTopicItems(prev => {
                              const next = new Set(prev);
                              if (next.has(result.item.id)) {
                                next.delete(result.item.id);
                              } else {
                                next.add(result.item.id);
                              }
                              return next;
                            });
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 shrink-0"
                        />
                        <Thumbnail
                          src={result.item.thumbnail_url}
                          alt={result.item.title ?? ""}
                          className="h-[45px] w-[80px] shrink-0 rounded object-cover"
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTopicResult(result);
                          }}
                          className="flex-1 min-w-0 text-left"
                        >
                          <p className="text-sm font-medium text-gray-900 line-clamp-1 hover:text-blue-600">{result.item.title}</p>
                          <p className="text-[11px] text-gray-500 line-clamp-1">
                            {result.item.channel_name && <span>{result.item.channel_name}</span>}
                            {result.item.channel_name && result.item.published_date && <span> · </span>}
                            {result.item.published_date && <span>{formatDate(result.item.published_date)}</span>}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="inline-flex items-center shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 max-w-[120px] truncate">
                              {result.segment.topic}
                            </span>
                            <span className="text-[10px] text-gray-400">
                              {Math.floor(result.segment.start_ms / 60000)}:{String(Math.floor((result.segment.start_ms % 60000) / 1000)).padStart(2, '0')}
                            </span>
                          </div>
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Right: Selected topic details */}
                  <div className="w-[480px] shrink-0 hidden md:block">
                    {selectedTopicResult ? (
                      <div className="sticky top-4 rounded-lg border bg-white p-4">
                        {/* Thumbnail */}
                        <Thumbnail
                          src={selectedTopicResult.item.thumbnail_url}
                          alt={selectedTopicResult.item.title ?? ""}
                          className="w-full h-[180px] rounded-lg object-cover mb-4"
                        />
                        
                        {/* Episode title */}
                        <h3 className="text-sm font-semibold text-gray-900">
                          {selectedTopicResult.item.title}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">{selectedTopicResult.item.channel_name}</p>
                        
                        {/* Topic badge */}
                        <div className="mt-3 flex items-center gap-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            🎯 {selectedTopicResult.segment.topic}
                          </span>
                          <span className="text-xs text-gray-400">
                            {Math.floor(selectedTopicResult.segment.start_ms / 60000)}:{String(Math.floor((selectedTopicResult.segment.start_ms % 60000) / 1000)).padStart(2, '0')} - {Math.floor(selectedTopicResult.segment.end_ms / 60000)}:{String(Math.floor((selectedTopicResult.segment.end_ms % 60000) / 1000)).padStart(2, '0')}
                          </span>
                        </div>
                        
                        {/* Summary */}
                        {selectedTopicResult.segment.summary && (
                          <div className="mt-4">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Summary</p>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {selectedTopicResult.segment.summary}
                            </p>
                          </div>
                        )}
                        
                        {/* Speakers */}
                        {selectedTopicResult.segment.speakers.length > 0 && (
                          <div className="mt-4">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Speakers</p>
                            <div className="flex flex-wrap gap-1">
                              {selectedTopicResult.segment.speakers.map((speaker, i) => (
                                <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">
                                  {speaker}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Play button - Issue 4: Include folder context */}
                        <button
                          onClick={() => navigate(`/transcript/${selectedTopicResult.item.id}?t=${selectedTopicResult.segment.start_ms}${selectedFolderId ? `&from_folder=${selectedFolderId}` : ''}`)}
                          className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          ▶️ Play from {Math.floor(selectedTopicResult.segment.start_ms / 60000)}:{String(Math.floor((selectedTopicResult.segment.start_ms % 60000) / 1000)).padStart(2, '0')}
                        </button>
                      </div>
                    ) : (
                      <div className="sticky top-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                        <p className="text-sm text-gray-500">Select a topic to see details</p>
                      </div>
                    )}
                  </div>
                </div>
              )
            )}
            
            {/* Regular Items List (hidden when topic search is active) */}
            {(!topicSearchMode || debouncedQuery.length < 2) && (
              !topicSearchMode && debouncedQuery.length >= 2 
                ? titleSearchResults 
                : filteredItems
            ).map((item) => (
              <div
                key={item.id}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setContextMenu({ itemId: item.id, x: e.clientX, y: e.clientY, showFolders: false });
                }}
                className="group flex items-center justify-between rounded-lg border bg-white px-4 py-3 transition hover:shadow-sm"
              >
                <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
                  <label className="flex items-center justify-center min-h-[44px] min-w-[44px] -ml-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item.id)}
                      onChange={() => toggleSelect(item.id)}
                      className="h-5 w-5 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </label>
                  <Thumbnail
                    src={getItemThumbnailUrl(item)}
                    fallbackSrc={sanitizeThumbnailUrl(item.channel_artwork_url)}
                    alt={item.title ?? ""}
                    className={`shrink-0 rounded-md object-cover ${
                      item.source === "rss"
                        ? "h-[52px] w-[52px] sm:h-[68px] sm:w-[68px]"
                        : "h-[52px] w-[92px] sm:h-[68px] sm:w-[120px]"
                    }`}
                  />
                  <button
                    onClick={() => navigate(`/transcript/${item.id}${selectedFolderId ? `?from_folder=${selectedFolderId}` : ''}`)}
                    className="min-w-0 flex-1 text-left min-h-[44px]"
                  >
                    <p className="text-xs sm:text-sm font-medium text-gray-900">
                      {item.title}
                    </p>
                    {item.was_truncated && (
                      <span className="mt-1 inline-flex w-fit rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                        Partial transcript
                      </span>
                    )}
                    <p className="mt-0.5 text-[10px] sm:text-xs text-gray-500 flex items-center gap-1">
                      {item.channel_name && (
                        <span className="font-medium text-gray-600">{item.channel_name}</span>
                      )}
                      {(item.video_id && !item.video_id.startsWith("rss_") || item.youtube_url) && (
                        <>
                          {item.channel_name && <span>&middot;</span>}
                          <a
                            href={item.youtube_url || `https://www.youtube.com/watch?v=${item.video_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center text-red-500 hover:text-red-600 transition-colors"
                            title="Watch on YouTube"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              className="h-3 w-3 sm:h-3.5 sm:w-3.5"
                            >
                              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                            </svg>
                          </a>
                        </>
                      )}
                      <span>&middot;</span>
                      <span>{item.published_date ? formatDate(item.published_date) : "—"}</span>
                      {item._durationStr && (
                        <>
                          <span>&middot;</span>
                          <span>{item._durationStr}</span>
                        </>
                      )}
                    </p>
                    <p className="mt-0.5 text-[10px] text-gray-400 text-right">
                      Transcribed {formatDate(item.created_at)}
                    </p>
                  </button>
                </div>
                {/* Desktop-only action buttons (shown on hover) */}
                <div className="ml-4 hidden items-center gap-2 md:group-hover:flex">
                  {/* Move to folder dropdown */}
                  <div className="relative" ref={moveDropdownId === item.id ? moveDropdownRef : undefined}>
                    <button
                      onClick={() => setMoveDropdownId(moveDropdownId === item.id ? null : item.id)}
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center text-xs text-gray-400 hover:text-blue-500"
                      aria-label={`Move ${item.title} to folder`}
                      title="Move to folder"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                        <path d="M3.75 3A1.75 1.75 0 002 4.75v3.26a3.235 3.235 0 011.75-.51h12.5c.644 0 1.245.188 1.75.51V6.75A1.75 1.75 0 0016.25 5h-4.836a.25.25 0 01-.177-.073L9.823 3.513A1.75 1.75 0 008.586 3H3.75zM3.75 9A1.75 1.75 0 002 10.75v4.5c0 .966.784 1.75 1.75 1.75h12.5A1.75 1.75 0 0018 15.25v-4.5A1.75 1.75 0 0016.25 9H3.75z" />
                      </svg>
                    </button>
                    {moveDropdownId === item.id && (
                      <div className="absolute right-0 z-20 mt-1 w-44 rounded-md border bg-white py-1 shadow-lg">
                        {folders.length === 0 ? (
                          <p className="px-3 py-2 text-xs text-gray-400">No folders</p>
                        ) : (
                          folders.map((f) => (
                            <button
                              key={f.id}
                              onClick={() => moveItemToFolder(item.id, f.id)}
                              className="block w-full min-h-[44px] px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                            >
                              {f.name}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  {selectedFolderId && (
                    <button
                      onClick={() => removeFromFolder(item.id)}
                      className="min-h-[44px] px-2 text-xs text-gray-400 hover:text-orange-500"
                      aria-label={`Remove ${item.title} from folder`}
                    >
                      Remove
                    </button>
                  )}
                  <button
                    onClick={() => setDeleteConfirm({ id: item.id, title: item.title })}
                    className="min-h-[44px] px-2 text-xs text-gray-400 hover:text-red-500"
                    aria-label={`Delete ${item.title}`}
                  >
                    Delete
                  </button>
                </div>
                {/* Mobile: tap row to select, use batch actions at top */}
              </div>
            ))}
            
            {/* Load more button */}
            {hasMore && !topicSearchMode && (
              <div className="flex justify-center pt-4 pb-2">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {loadingMore ? "Loading..." : "Load more"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Right-click context menu */}
        {contextMenu && (
          <div
            ref={contextRef}
            className="fixed z-30 w-48 rounded-md border bg-white py-1 shadow-lg"
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            <button
              onClick={() => navigate(`/transcript/${contextMenu.itemId}${selectedFolderId ? `?from_folder=${selectedFolderId}` : ''}`)}
              className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
            >
              Open
            </button>
            <div className="relative">
              <button
                onClick={() => setContextMenu({ ...contextMenu, showFolders: !contextMenu.showFolders })}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
              >
                Move to folder
                <span className="text-xs text-gray-400">&rsaquo;</span>
              </button>
              {contextMenu.showFolders && (
                <div className="absolute left-full top-0 ml-1 w-44 rounded-md border bg-white py-1 shadow-lg">
                  {folders.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-gray-400">No folders</p>
                  ) : (
                    folders.map((f) => (
                      <button
                        key={f.id}
                        onClick={() => moveToFolder(contextMenu.itemId, f.id)}
                        className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {f.name}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            {selectedFolderId && (
              <button
                onClick={() => {
                  removeFromFolder(contextMenu.itemId);
                  setContextMenu(null);
                }}
                className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
              >
                Remove from folder
              </button>
            )}
            <div className="my-1 h-px bg-gray-100" />
            <button
              onClick={() => {
                const item = items.find((i) => i.id === contextMenu.itemId);
                setDeleteConfirm({ id: contextMenu.itemId, title: item?.title ?? "this transcript" });
                setContextMenu(null);
              }}
              className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
            >
              Delete
            </button>
          </div>
        )}
        {/* Delete confirmation modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
            <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
              <h2 className="text-lg font-semibold text-gray-900">Delete transcript?</h2>
              <p className="mt-2 text-sm text-gray-500">
                Delete &ldquo;{deleteConfirm.title}&rdquo;? This cannot be undone.
              </p>
              <div className="mt-5 flex items-center gap-3">
                <button
                  onClick={confirmDelete}
                  className="flex-1 min-h-[44px] rounded-lg bg-red-600 py-2.5 text-sm font-medium text-white hover:bg-red-700"
                >
                  Delete
                </button>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 min-h-[44px] rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Bulk delete confirmation modal */}
        {bulkDeleteConfirm && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
            <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl mx-4">
              <h2 className="text-lg font-semibold text-gray-900">Delete {selectedIds.size} transcripts?</h2>
              <p className="mt-2 text-sm text-gray-500">
                This will permanently delete {selectedIds.size} selected transcript{selectedIds.size > 1 ? "s" : ""}. This cannot be undone.
              </p>
              <div className="mt-5 flex items-center gap-3">
                <button
                  onClick={confirmBulkDelete}
                  className="flex-1 min-h-[44px] rounded-lg bg-red-600 py-2.5 text-sm font-medium text-white hover:bg-red-700"
                >
                  Delete All
                </button>
                <button
                  onClick={() => setBulkDeleteConfirm(false)}
                  className="flex-1 min-h-[44px] rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
